import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box
} from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useNotificationStack } from '../../app/hooks/useNotificationStack'
import {
  useAddWorkforceTypeMutation,
  useUpdateWorkforceTypeMutation,
} from '../../app/redux-fetch/apiUser'
import SubmitButtons from '../../components/Buttons/SubmitButtons'
import FormInput from '../../components/Forms/FormInput'
import SideModalWrapper from '../Assets/modals/SideModalWrapper'
import { messages } from './messages'
import { WorkforceTypeFormData, workforceTypeSchema } from './schema'
import { IWorkforceType } from './types'


type WorkforceTypeFormProps = {
  workforceType: IWorkforceType
  handleClose: () => void
  isOpen: boolean
}

dayjs.extend(utc)


const WorkforceTypeForm = ({ workforceType, handleClose, isOpen }: WorkforceTypeFormProps) => {
  const { formatMessage } = useIntl()
  const isNew = !workforceType?.id || workforceType?.id === 0

  const [addWorkforceType] = useAddWorkforceTypeMutation()
  const [updateWorkforceType] = useUpdateWorkforceTypeMutation()
  const { enqueueSuccess, enqueueFailure } = useNotificationStack()

  const transformedWorkforceType = {
    ...workforceType,
    validFrom: workforceType.validFrom
  };

  const methods = useForm<WorkforceTypeFormData>({
    resolver: zodResolver(workforceTypeSchema),
    defaultValues: transformedWorkforceType,
  })

  const {
    handleSubmit,
  } = methods


  const onSubmit = async (data: WorkforceTypeFormData) => {
    const apiData = {
      ...data,
      validFrom: data.validFrom
    } as IWorkforceType;

    if (isNew) {
      await addWorkforceType({ tenantId: data.tenantId, body: apiData })
        .then(() => {
          enqueueSuccess(messages.workforceTypeCreated)
          handleClose()
        })
        .catch(() => enqueueFailure(messages.workforceTypeNotAdded))
    } else {
      await updateWorkforceType({ tenantId: data.tenantId, body: apiData })
        .then(() => {
          enqueueSuccess(messages.workforceTypeUpdated)
          handleClose()
        })
        .catch(() => enqueueFailure(messages.workforceTypeNotUpdated))
    }
  }

  const onSubmitHandler = handleSubmit(onSubmit)


  return (
    <SideModalWrapper
      open={isOpen}
      onClose={handleClose}
      headerLabel={formatMessage(messages.createEditWorkforceType)}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ minWidth: '450px', m: '32px' }}>
            <FormInput
              name="name"
              label={formatMessage(messages.name)}
              required
              maxLength={100}
            />
            <FormInput
              name="description"
              label={formatMessage(messages.description)}
              multiline
              rows={4}
              maxLength={100}
            />
          </Box>
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              width: '100%',
              padding: '16px',
              backgroundColor: '#fff',
              borderTop: '1px solid #e0e0e0',
              zIndex: 999,
            }}
          >
            <SubmitButtons create={isNew} isEnabled={true} onCancel={handleClose} submitForm={onSubmitHandler}
            />
          </Box>
        </form>
      </FormProvider>
    </SideModalWrapper>
  )
}

export default WorkforceTypeForm
